import { apiClient } from '../lib/apiClient';
import type {
  AnalysisMode,
  ApplyRequest,
  ApplyResultItem,
  CaptureDto,
  CaptureSuggestionDto,
  CaptureSummaryDto,
  VoiceSettingsDto,
} from '../types/capture';

/**
 * Voice journal (docs/voiceFeature.md): voice notes ("captures"), the "To
 * review" inbox and the task suggestions of typed journal entries. Nothing is
 * saved without the user's confirmation — suggestions only become records
 * through applyCapture / applyEntryTaskSuggestions.
 */

export interface UploadVoiceCaptureInput {
  aquariumId: string;
  audio: Blob;
  mimeType: string;
  /** Original recording time (ISO) — kept for notes recorded offline. */
  recordedAt: string;
  durationMs: number;
  /** UI language or the language chosen for this recording. */
  language?: string;
  /** Credits used up: store and transcribe now, analyse once credits are available. */
  deferAnalysis?: boolean;
  /** 'transcript' = "Text only, no AI" (one journal entry from the transcript). */
  analysisMode?: AnalysisMode;
  /** Offline-queue item id; a retried upload returns the same capture. */
  idempotencyKey: string;
}

const EXTENSION_BY_MIME: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/ogg': 'ogg',
  'audio/mp4': 'm4a',
  'audio/x-m4a': 'm4a',
  'audio/mpeg': 'mp3',
};

/** 'audio/webm;codecs=opus' → 'audio/webm'. */
export function baseMimeType(mimeType: string): string {
  return mimeType.split(';')[0]!.trim().toLowerCase();
}

export async function getVoiceSettings(): Promise<VoiceSettingsDto> {
  const response = await apiClient.get<{ data: VoiceSettingsDto }>('/api/v1/me/voice');
  return response.data;
}

export async function giveVoiceConsent(version: string): Promise<VoiceSettingsDto['consent']> {
  const response = await apiClient.post<{ data: VoiceSettingsDto['consent'] }>('/api/v1/me/voice/consent', { version });
  return response.data;
}

export async function withdrawVoiceConsent(): Promise<void> {
  await apiClient.delete('/api/v1/me/voice/consent');
}

export async function uploadVoiceCapture(input: UploadVoiceCaptureInput): Promise<CaptureDto> {
  const mime = baseMimeType(input.mimeType);
  const form = new FormData();
  form.append('audio', input.audio, `note.${EXTENSION_BY_MIME[mime] ?? 'webm'}`);
  form.append('recordedAt', input.recordedAt);
  form.append('durationMs', String(Math.round(input.durationMs)));
  if (input.language) form.append('language', input.language);
  if (input.deferAnalysis) form.append('deferAnalysis', 'true');
  if (input.analysisMode === 'transcript') form.append('analysisMode', 'transcript');
  const response = await apiClient.post<{ data: CaptureDto }>(
    `/api/v1/aquariums/${encodeURIComponent(input.aquariumId)}/voice-captures`,
    form,
    {
      headers: { 'Idempotency-Key': input.idempotencyKey },
      // As a query parameter too: the API's budget check runs before the multipart body is read.
      ...(input.deferAnalysis ? { params: { deferAnalysis: 'true' } } : {}),
    },
  );
  return response.data;
}

export async function listCaptures(): Promise<CaptureDto[]> {
  const response = await apiClient.get<{ data: CaptureDto[] }>('/api/v1/captures');
  return response.data;
}

export async function getCaptureSummary(): Promise<CaptureSummaryDto> {
  const response = await apiClient.get<{ data: CaptureSummaryDto }>('/api/v1/captures/summary');
  return response.data;
}

export async function getCapture(captureId: string): Promise<CaptureDto> {
  const response = await apiClient.get<{ data: CaptureDto }>(`/api/v1/captures/${encodeURIComponent(captureId)}`);
  return response.data;
}

export interface UpdateCaptureInput {
  transcript?: string;
  aquariumId?: string;
}

export async function updateCapture(captureId: string, input: UpdateCaptureInput): Promise<CaptureDto> {
  const response = await apiClient.patch<{ data: CaptureDto }>(`/api/v1/captures/${encodeURIComponent(captureId)}`, input);
  return response.data;
}

export async function reanalyseCapture(
  captureId: string,
  input: { transcript?: string; language?: string } = {},
): Promise<CaptureDto> {
  const response = await apiClient.post<{ data: CaptureDto }>(
    `/api/v1/captures/${encodeURIComponent(captureId)}/reanalyse`,
    input,
  );
  return response.data;
}

export interface ApplyCaptureResult {
  capture: CaptureDto;
  results: ApplyResultItem[];
}

export async function applyCapture(captureId: string, request: ApplyRequest): Promise<ApplyCaptureResult> {
  const response = await apiClient.post<{ data: ApplyCaptureResult }>(
    `/api/v1/captures/${encodeURIComponent(captureId)}/apply`,
    request,
  );
  return response.data;
}

export async function undoCapture(captureId: string): Promise<CaptureDto> {
  const response = await apiClient.post<{ data: CaptureDto }>(`/api/v1/captures/${encodeURIComponent(captureId)}/undo`);
  return response.data;
}

export async function retryCapture(captureId: string): Promise<CaptureDto> {
  const response = await apiClient.post<{ data: CaptureDto }>(`/api/v1/captures/${encodeURIComponent(captureId)}/retry`);
  return response.data;
}

export async function discardCapture(captureId: string): Promise<void> {
  await apiClient.delete(`/api/v1/captures/${encodeURIComponent(captureId)}`);
}

// ─── Typed journal entries (UC-B) ─────────────────────────────────────────────

function entryPath(aquariumId: string, entryId: string): string {
  return `/api/v1/aquariums/${encodeURIComponent(aquariumId)}/journal-entries/${encodeURIComponent(entryId)}`;
}

export async function detectEntryTasks(aquariumId: string, entryId: string): Promise<CaptureSuggestionDto[]> {
  const response = await apiClient.post<{ data: { suggestions: CaptureSuggestionDto[] } }>(
    `${entryPath(aquariumId, entryId)}/detect-tasks`,
  );
  return response.data.suggestions;
}

export async function listEntryTaskSuggestions(aquariumId: string, entryId: string): Promise<CaptureSuggestionDto[]> {
  const response = await apiClient.get<{ data: { suggestions: CaptureSuggestionDto[] } }>(
    `${entryPath(aquariumId, entryId)}/task-suggestions`,
  );
  return response.data.suggestions;
}

export interface ApplyEntryTasksResult {
  results: ApplyResultItem[];
  suggestions: CaptureSuggestionDto[];
}

export async function applyEntryTaskSuggestions(
  aquariumId: string,
  entryId: string,
  request: Pick<ApplyRequest, 'items' | 'dismiss'>,
): Promise<ApplyEntryTasksResult> {
  const response = await apiClient.post<{ data: ApplyEntryTasksResult }>(
    `${entryPath(aquariumId, entryId)}/task-suggestions/apply`,
    request,
  );
  return response.data;
}
